// src/components/forms/variants/v2/base/ErrorBoundary.tsx

'use client'

import { Component, type ReactNode } from 'react'
import RiErrorWarningLine from "~/icons/remix/generated/RiErrorWarningLine"

import { handleClientError } from '~/lib/client-error-handling'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
}

export class FormErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: { componentStack?: string }): void {
    // Classify + log to the drain (structured `client_error`) with boundary
    // recovery for transient chunk/DOM faults. Mirrors ResourceErrorBoundary.
    handleClientError(
      error,
      {
        type: 'error_boundary',
        component: 'FormErrorBoundary',
        componentStack: errorInfo.componentStack,
      },
      // Log-only (no reload): preserves the pre-PR inline-fallback behavior.
      // Important for email-capture forms — auto-reloading on a transient
      // chunk_load fault would discard in-progress user input.
      { recover: false },
    )
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback ?? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900/50 dark:bg-red-900/20">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-red-100 p-1 dark:bg-red-900/50">
              <RiErrorWarningLine className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Something went wrong
              </h3>
              {this.state.error && (
                <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                  {this.state.error.message}
                </p>
              )}
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
} 